import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuthStore } from "./auth";
import fr from "../locales/fr.json";
import en from "../locales/en.json";

export type Locale = "fr" | "en";

const messages: Record<Locale, any> = { fr, en };

/**
 * Centralized locale store.
 * - Persists choice in localStorage (fallback) + user's Firestore profile
 * - Watches auth.profile.locale to sync across devices for logged-in users
 * - Exposes t(key, params) translation function
 *
 * Key format: "section.subsection.key" (dot-notation path into JSON)
 * Interpolation: t("key", { name: "Maxime" }) replaces {name} in the message.
 */
export const useI18nStore = defineStore("i18n", () => {
  // Priority: localStorage → browser language → default 'fr'
  const initial = (): Locale => {
    const saved = localStorage.getItem("maxglasstrack_locale") as Locale | null;
    if (saved === "fr" || saved === "en") return saved;
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith("en")) return "en";
    return "fr";
  };

  const locale = ref<Locale>(initial());

  function t(key: string, params?: Record<string, any>): string {
    const parts = key.split(".");
    let msg: any = messages[locale.value];
    for (const p of parts) {
      if (msg == null) break;
      msg = msg[p];
    }
    if (msg == null) {
      // Fallback to English if key missing in current locale
      let fallback: any = messages.en;
      for (const p of parts) {
        if (fallback == null) break;
        fallback = fallback[p];
      }
      msg = fallback ?? key;
    }
    if (typeof msg !== "string") return key;
    if (!params) return msg;
    return msg.replace(/\{(\w+)\}/g, (_, k) => params[k] ?? `{${k}}`);
  }

  /** Get an array from locale (e.g. feature lists) */
  function ta(key: string): string[] {
    const parts = key.split(".");
    let msg: any = messages[locale.value];
    for (const p of parts) {
      if (msg == null) break;
      msg = msg[p];
    }
    if (Array.isArray(msg)) return msg;
    return [];
  }

  async function setLocale(newLocale: Locale) {
    locale.value = newLocale;
    localStorage.setItem("maxglasstrack_locale", newLocale);
    // Sync to Firestore profile if logged in
    const auth = useAuthStore();
    if (auth.user) {
      try {
        await updateDoc(doc(db, "users", auth.user.uid), { locale: newLocale });
      } catch (e) {
        console.warn("Could not sync locale to Firestore:", e);
      }
    }
    // Update <html lang> for accessibility
    document.documentElement.lang = newLocale;
  }

  // When auth profile loads, apply its stored locale preference
  function syncFromProfile() {
    const auth = useAuthStore();
    watch(
      () => auth.profile?.locale,
      (profileLocale) => {
        if (profileLocale && (profileLocale === "fr" || profileLocale === "en")) {
          if (profileLocale !== locale.value) {
            locale.value = profileLocale;
            localStorage.setItem("maxglasstrack_locale", profileLocale);
            document.documentElement.lang = profileLocale;
          }
        }
      },
      { immediate: true }
    );
  }

  // Initial html lang set
  document.documentElement.lang = locale.value;

  const isFrench = computed(() => locale.value === "fr");
  const isEnglish = computed(() => locale.value === "en");

  return { locale, t, ta, setLocale, syncFromProfile, isFrench, isEnglish };
});
